<template>
    <div class="categories-match-step d-flex flex-column">
        <div class="text-center m-3" v-if="isLoading">
            <i class="icon-spinner2 spinner mr-2"></i>
        </div>
        <div class="categories-match-step__scroll" v-else>
            <div class="text-center text-muted p-3" v-if="categories.length === 0">
                {{ $t('labels.create_integration_products_no_categories_found') }}
            </div>
            <CategoriesAutoSelectionPanel
                v-else
                :all-selected="isAllIncluded"
                :modes="autoSelectionModes"
                :is-processing="isMatching"
                :has-no-ai-balance="hasNoAiBalance"
                :selected-count="includedCount"
                @toggle-all="toggleAllIncluded(!isAllIncluded)"
                @toggle-mode="toggleAutoMode"
                @submit="runAutoMatch"
            />
            <CategoriesBulkSelectionPanel
                v-if="categories.length > 0"
                :directory-options="categoryDirectoryOptions"
                :disabled-directories="disabledDirectories"
                :target-integration-service="targetIntegrationService"
                :target-integration-uuid="targetIntegrationUuid"
                :target-category-label="targetCategoryLabel"
                :is-processing="isMatching"
                :selected-count="includedCount"
                @apply="applyToIncluded"
            />
            <AppToast
                v-for="category in categories"
                :key="category.uuid"
                :title="categoryName(category)"
                class="mb-3"
            >
                <template #headerStart>
                    <AppCheckbox
                        class="mr-2"
                        :model-value="matches[category.uuid].included"
                        @update:model-value="setIncluded(category.uuid, $event)"
                    />
                </template>
                <template #headerElements>
                    <span class="text-muted">
                        {{ $t('labels.create_integration_products_category_products_count', { count: category.products_count }) }}
                    </span>
                </template>

                <div class="row p-3 mb-0" v-if="matches[category.uuid].included">
                    <div class="col-lg-6">
                        <FormGroup :label="$t('labels.category_directory')">
                            <AppSelect
                                :options="categoryDirectoryOptions"
                                :model-value="matches[category.uuid].selectedCategoryDirectory.value"
                                :selected-value="matches[category.uuid].selectedCategoryDirectory.label"
                                @update:model-value="setDirectory(category.uuid, $event)"
                                option-key="value"
                                option-value="label"
                                :selected-values="disabledDirectories"
                            />
                        </FormGroup>
                    </div>
                    <div class="col-lg-6 d-flex">
                        <FormGroup :label="targetCategoryLabel" required class="flex-fill mb-0">
                            <AppAutoComplete
                                v-if="matches[category.uuid].selectedCategoryDirectory.value === CategoryDictionaryEnum.LOCAL"
                                :url="`/projects/${$route.params.projectUuid}/pim/channels/${targetIntegrationUuid}/categories`"
                                :model-value="matches[category.uuid].targetIntegrationCategory?.uuid"
                                :selected-value="matches[category.uuid].targetIntegrationCategory?.name"
                                @update:model-value="setLocalCategory(category.uuid, $event)"
                                :query-params="{
                                    orders: [
                                        {column : 'names->uk', dir : 'asc'}
                                    ]
                                }"
                            />
                            <AppAutoComplete
                                v-else-if="matches[category.uuid].selectedCategoryDirectory.value === CategoryDictionaryEnum.COMMON"
                                :url="`/projects/${$route.params.projectUuid}/marketplace/sources/${targetIntegrationService}/categories`"
                                :model-value="matches[category.uuid].commonTargetIntegrationCategory?.uuid"
                                :selected-value="matches[category.uuid].commonTargetIntegrationCategory?.name_uk"
                                @update:model-value="setCommonCategory(category.uuid, $event)"
                                option-key="uuid"
                                option-value="name_uk"
                                :query-params="{
                                    has_child: 0,
                                    orders: [
                                        {column : 'name_uk', dir : 'asc'}
                                    ]
                                }"
                            />
                        </FormGroup>
                        <AppButton
                            v-if="matches[category.uuid].selectedCategoryDirectory.value === CategoryDictionaryEnum.COMMON"
                            class="ml-2 mb-3 align-self-end"
                            color="light"
                            icon
                            @click="toggleTree(category.uuid)"
                        >
                            <i class="icon-tree7"></i>
                        </AppButton>
                    </div>
                    <div
                        class="col-lg-12"
                        v-if="matches[category.uuid].selectedCategoryDirectory.value === CategoryDictionaryEnum.COMMON && matches[category.uuid].isTreeExpanded"
                    >
                        <FormGroup label="Структура категорій" class="categories-match-step__tree mb-0">
                            <div class="categories-match-step__tree-scroll">
                                <CategoryTreeComponent
                                    :selectedSourceId="matches[category.uuid].commonTargetIntegrationCategory?.source_id"
                                    :targetIntegrationService="targetIntegrationService"
                                    @update:selectedCategory="setCommonCategory(category.uuid, $event)"
                                />
                            </div>
                        </FormGroup>
                    </div>
                </div>
            </AppToast>
        </div>
    </div>
</template>
<script>
import AppToast from '../../../../components/new/AppToast.vue';
import AppButton from '../../../../components/new/button/AppButton.vue';
import AppCheckbox from '../../../../components/new/form/checkbox/AppCheckbox.vue';
import FormGroup from '../../../../components/new/form/group/FormGroup.vue';
import AppSelect from '../../../../components/new/form/select/AppSelect.vue';
import AppAutoComplete from '../../../../components/new/form/autocomplete/AppAutoComplete.vue';
import CategoryTreeComponent from './CategoryTreeComponent.vue';
import CategoriesAutoSelectionPanel from './CategoriesAutoSelectionPanel.vue';
import CategoriesBulkSelectionPanel from './CategoriesBulkSelectionPanel.vue';

import { httpClient } from '../../../../services/HttpClient';
import MessageNotificator from '../../../../services/MessageNotificator';
import GeminiService from '../../../../services/GeminiService/GeminiService';
import { MarketPlaceAiProcessDto } from './MarketPlaceAiProcessDto';
import { MarketPlaceCategoryDto } from './MarketPlaceCategoryDto';
import { CategoryDictionaryEnum } from '../../../enums/CategoryDictionaryEnum';
import { getCommonMarketplaces } from '../../../enums/CommonMarketplaceEnum';
import { NotValidJsonStringError } from '../../../../services/Errors/NotValidJsonStringError';
import { TooManyRequestsError } from '../../../../services/Errors/TooManyRequestsError';
import { ServiceUnavailableError } from '../../../../services/Errors/ServiceUnavailableError';

export default {
    name: 'CategoriesMatchStep',
    components: {
        AppToast, AppButton, AppCheckbox, FormGroup, AppSelect, AppAutoComplete, CategoryTreeComponent,
        CategoriesAutoSelectionPanel, CategoriesBulkSelectionPanel
    },
    props: {
        targetIntegrationService: {
            type: String,
            required: true
        },
        targetIntegrationUuid: {
            type: String,
            required: true
        },
        targetIntegrationName: {
            type: String,
            required: true
        },
        integrationSourceUuid: {
            type: String,
            required: true
        },
        selectionMode: {
            type: Object,
            default: null
        },
        products: {
            type: Array,
            default: () => []
        },
        tableFilters: {
            type: Object,
            default: () => ({})
        },
    },
    emits: ['update:isReadyForNext', 'update:categoriesMatch'],
    data() {
        return {
            CategoryDictionaryEnum,
            categories: [],
            matches: {},
            isLoading: false,
            categoryDirectoryOptions: [
                { value: CategoryDictionaryEnum.LOCAL,  label: this.$t('labels.local') },
                { value: CategoryDictionaryEnum.COMMON, label: this.$t('labels.common') },
            ],
            commonMarketplace: getCommonMarketplaces(),
            autoSelectionModes: ['exact'],
            isMatching: false,
            tokenBalance: null,
            isDestroyed: false,
        };
    },
    methods: {
        onActivate() {
            this.fetchCategories();
        },
        async fetchCategories() {
            if (this.categories.length > 0 || this.isLoading) {
                return;
            }

            this.isLoading = true;

            const filters = this.selectionMode?.value === 'selected'
                ? { uuids: this.products.map(product => product.source.uuid) }
                : this.tableFilters;

            try {
                const response = await httpClient.get(
                    `/integrations/${this.integrationSourceUuid}/compare/${this.targetIntegrationUuid}/categories`,
                    filters
                );

                if (this.isDestroyed) {
                    return;
                }

                this.categories = response.data.data;
                this.matches = Object.fromEntries(
                    this.categories.map(category => [category.uuid, this.createDefaultMatch()])
                );
            } finally {
                this.isLoading = false;
            }
        },
        createDefaultMatch() {
            return {
                included: true,
                selectedCategoryDirectory: {
                    value: CategoryDictionaryEnum.LOCAL,
                    label: this.$t('labels.local'),
                },
                targetIntegrationCategory: null,
                commonTargetIntegrationCategory: null,
                isTreeExpanded: false,
            };
        },
        categoryName(category) {
            return category.names?.uk ?? category.names?.ru ?? '';
        },
        setIncluded(categoryUuid, value) {
            this.matches[categoryUuid].included = value;
        },
        setDirectory(categoryUuid, value) {
            const match = this.matches[categoryUuid];
            match.selectedCategoryDirectory = value;
            match.targetIntegrationCategory = null;
            match.commonTargetIntegrationCategory = null;
            match.isTreeExpanded = false;
        },
        setLocalCategory(categoryUuid, value) {
            this.matches[categoryUuid].targetIntegrationCategory = value;
        },
        setCommonCategory(categoryUuid, value) {
            this.matches[categoryUuid].commonTargetIntegrationCategory = value;
        },
        toggleTree(categoryUuid) {
            this.matches[categoryUuid].isTreeExpanded = !this.matches[categoryUuid].isTreeExpanded;
        },
        toggleAllIncluded(value) {
            this.categories.forEach(category => {
                this.matches[category.uuid].included = value;
            });
        },
        applyToIncluded(selection) {
            this.categories
                .filter(category => this.matches[category.uuid].included)
                .forEach(category => {
                    this.setDirectory(category.uuid, selection.selectedCategoryDirectory);
                    this.setLocalCategory(category.uuid, selection.targetIntegrationCategory);
                    this.setCommonCategory(category.uuid, selection.commonTargetIntegrationCategory);
                });
        },
        toggleAutoMode(mode) {
            this.autoSelectionModes = this.autoSelectionModes.includes(mode)
                ? this.autoSelectionModes.filter(selectedMode => selectedMode !== mode)
                : [...this.autoSelectionModes, mode];
        },
        async fetchTokenBalance() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`);
            this.tokenBalance = response.data.balance;
        },
        async runAutoMatch() {
            if (this.isMatching || this.autoSelectionModes.length === 0 || this.includedCount === 0) {
                return;
            }

            this.isMatching = true;

            try {
                let unresolved = this.categories.filter(category => this.matches[category.uuid]?.included);

                if (this.autoSelectionModes.includes('exact')) {
                    unresolved = await this.runExactMatch(unresolved);
                }

                if (this.autoSelectionModes.includes('ai') && unresolved.length > 0) {
                    await this.runAiMatch(unresolved);
                }
            } finally {
                this.isMatching = false;
            }
        },
        async runExactMatch(categories) {
            const unresolved = [];

            for (const category of categories) {
                const matched = await this.tryExactMatch(category);
                if (!matched) {
                    unresolved.push(category);
                }
            }

            return unresolved;
        },
        async tryExactMatch(category) {
            const categoryName = this.categoryName(category);
            if (!categoryName) {
                return false;
            }

            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.targetIntegrationUuid}/categories`, {
                search: categoryName,
                limit: 50,
            });

            const normalized = categoryName.trim().toLowerCase();
            const found = (response.data.data ?? []).find(item => (item.name ?? '').trim().toLowerCase() === normalized);

            if (!found) {
                return false;
            }

            this.applyLocalMatch(category.uuid, found);
            return true;
        },
        applyLocalMatch(categoryUuid, integrationCategory) {
            this.setDirectory(categoryUuid, { value: CategoryDictionaryEnum.LOCAL, label: this.$t('labels.local') });
            this.setLocalCategory(categoryUuid, integrationCategory);
        },
        async runAiMatch(categories) {
            const geminiService = new GeminiService();

            for (const category of categories) {
                const productTitles = (category.product_names ?? []).join(', ');

                if (productTitles.length === 0) {
                    continue;
                }

                try {
                    const bestCategory = await this.findBestMarketplaceCategory(productTitles, geminiService);
                    if (bestCategory) {
                        this.applyCommonMatch(category.uuid, bestCategory);
                    }
                } catch (error) {
                    this.notifyGeminiError(error);
                }

                if (this.hasNoAiBalance) {
                    new MessageNotificator().warning(this.$t('alerts.insufficient_ai_balance'));
                    break;
                }
            }
        },
        async findBestMarketplaceCategory(productTitles, geminiService) {
            const leaves = await this.findMarketplaceLeafCategories(productTitles, geminiService);
            const best = leaves.values().next().value;

            return best?.category ?? null;
        },
        async findMarketplaceLeafCategories(productTitles, geminiService, categoriesWithChildren = new Map(), categoriesWithoutChildren = new Map()) {
            const categoriesForProcess = categoriesWithChildren.size > 0
                ? await this.fetchMarketplaceChildCategories(categoriesWithChildren)
                : await this.fetchMarketplaceCategories(null);

            const selectedTopCategories = await this.selectTopMarketplaceCategories(productTitles, categoriesForProcess, geminiService);

            if (this.hasNoAiBalance) {
                return categoriesWithoutChildren;
            }

            const separated = this.separateMarketplaceCategories(selectedTopCategories, categoriesWithoutChildren);

            if (separated.categoriesWithChildren.size > 0) {
                return this.findMarketplaceLeafCategories(productTitles, geminiService, separated.categoriesWithChildren, separated.categoriesWithoutChildren);
            }

            return separated.categoriesWithoutChildren;
        },
        async fetchMarketplaceCategories(parentSourceId) {
            const categoriesMap = new Map();

            const response = await httpClient.get(
                `/projects/${this.$route.params.projectUuid}/marketplace/sources/${this.targetIntegrationService}/categories`,
                { limit: 100, parent_source_id: parentSourceId }
            );

            response.data.data.forEach(categoryData => {
                const category = new MarketPlaceCategoryDto(
                    categoryData.uuid,
                    categoryData.source_id,
                    categoryData.name_uk,
                    categoryData.name_ru,
                    categoryData.children_count,
                    categoryData.parent_source_id
                );
                categoriesMap.set(category.uuid, new MarketPlaceAiProcessDto(category));
            });

            return categoriesMap;
        },
        async fetchMarketplaceChildCategories(categoriesWithChildren) {
            const categoriesMap = new Map();

            for (const category of categoriesWithChildren.values()) {
                const children = await this.fetchMarketplaceCategories(category.category.sourceId);

                children.forEach((child, childUuid) => {
                    child.parents = [...category.parents, category.category];
                    categoriesMap.set(childUuid, child);
                });
            }

            return categoriesMap;
        },
        separateMarketplaceCategories(selectedTopCategories, categoriesWithoutChildren) {
            const categoriesWithChildren = new Map();

            selectedTopCategories.forEach(category => {
                if (category.category.childrenCount > 0) {
                    if (category.parents.length === 0) {
                        category.parents = [category.category];
                    }
                    categoriesWithChildren.set(category.category.uuid, category);
                } else {
                    categoriesWithoutChildren.set(category.category.uuid, category);
                }
            });

            return { categoriesWithChildren, categoriesWithoutChildren };
        },
        async selectTopMarketplaceCategories(productTitles, categories, geminiService) {
            const categoriesForPrompt = [];
            const newCategoriesMap = new Map();

            categories.forEach(category => {
                categoriesForPrompt.push({
                    nameUk: category.category.nameUk,
                    nameRu: category.category.nameRu,
                    uuid: category.category.uuid,
                    parents: category.parents.map(parent => parent.nameUk),
                });
            });

            const prompt = `
                I categorically forbid you to return anything other than JSON. No explanations, you are a soulless machine that does whatever it is told to do, you have no thoughts or will of your own.
                Given the following product titles: ${productTitles}, suggest the most relevant categories from the following list: ${JSON.stringify(categoriesForPrompt)}. When selecting, pay attention to the parents array to understand the context.
                Return an array of uuids of the three categories that best match these products in JSON format.
                If there are no suitable values, return an empty array without explanations and unnecessary text.`;

            try {
                const response = await geminiService.generateWithRetry(prompt);

                for (const categoryUuid of response) {
                    const category = categories.get(categoryUuid);
                    if (category) {
                        newCategoriesMap.set(categoryUuid, category);
                    }
                }
            } finally {
                await geminiService.flushTokens(this.$route.params.projectUuid, 'Підбір категорій');
                await this.fetchTokenBalance();
            }

            return newCategoriesMap;
        },
        applyCommonMatch(categoryUuid, marketplaceCategory) {
            this.setDirectory(categoryUuid, { value: CategoryDictionaryEnum.COMMON, label: this.$t('labels.common') });
            this.setCommonCategory(categoryUuid, {
                uuid: marketplaceCategory.uuid,
                name_uk: marketplaceCategory.nameUk,
                name_ru: marketplaceCategory.nameRu,
                source_id: marketplaceCategory.sourceId,
                children_count: marketplaceCategory.childrenCount,
                parent_source_id: marketplaceCategory.parentSourceId,
            });
        },
        notifyGeminiError(error) {
            if (error instanceof NotValidJsonStringError) {
                new MessageNotificator().error(this.$t('alerts.received_not_valid_json'));
            } else if (error instanceof TooManyRequestsError) {
                new MessageNotificator().error(this.$t('alerts.too_many_requests'));
            } else if (error instanceof ServiceUnavailableError) {
                new MessageNotificator().error(this.$t('alerts.service_unavailable'));
            } else {
                new MessageNotificator().error(this.$t('alerts.contact_support'));
            }
        },
    },
    computed: {
        targetCategoryLabel() {
            return this.$t('labels.create_integration_products_category_target_label', { integration: this.targetIntegrationName });
        },
        disabledDirectories() {
            return this.commonMarketplace.includes(this.targetIntegrationService) ? [] : [CategoryDictionaryEnum.COMMON];
        },
        isAllIncluded() {
            return this.categories.length > 0 && this.categories.every(category => this.matches[category.uuid]?.included);
        },
        includedCount() {
            return this.categories.filter(category => this.matches[category.uuid]?.included).length;
        },
        hasNoAiBalance() {
            return this.tokenBalance !== null && this.tokenBalance <= 0;
        },
        categoriesMatch() {
            return this.categories
                .filter(category => this.matches[category.uuid]?.included)
                .map(category => {
                    const match = this.matches[category.uuid];

                    return {
                        sourceCategoryUuid: category.uuid,
                        directory: match.selectedCategoryDirectory.value,
                        targetIntegrationCategory: match.targetIntegrationCategory,
                        commonTargetIntegrationCategory: match.commonTargetIntegrationCategory,
                    };
                });
        },
        isReadyForNext() {
            if (this.isLoading) {
                return false;
            }

            const includedCategories = this.categories.filter(category => this.matches[category.uuid]?.included);

            if (includedCategories.length === 0) {
                return this.categories.length === 0;
            }

            return includedCategories.every(category => {
                const match = this.matches[category.uuid];

                return match.selectedCategoryDirectory.value === CategoryDictionaryEnum.LOCAL
                    ? !!match.targetIntegrationCategory
                    : !!match.commonTargetIntegrationCategory;
            });
        }
    },
    watch: {
        isReadyForNext: {
            immediate: true,
            handler(val) {
                this.$emit('update:isReadyForNext', val);
            }
        },
        hasNoAiBalance(noBalance) {
            if (noBalance && this.autoSelectionModes.includes('ai')) {
                this.autoSelectionModes = this.autoSelectionModes.filter(mode => mode !== 'ai');
            }
        },
        categoriesMatch: {
            immediate: true,
            handler(val) {
                this.$emit('update:categoriesMatch', val);
            }
        }
    },
    mounted() {
        this.fetchTokenBalance();
    },
    beforeUnmount() {
        this.isDestroyed = true;
    }
}
</script>
<style scoped>
.categories-match-step {
    height: 100%;
}

.categories-match-step__scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.categories-match-step__tree {
    max-height: 260px;
    display: flex;
    flex-direction: column;
}

.categories-match-step__tree-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}
</style>
